/**
 * Modèle pour Stripe Connect
 */

import { UHttp } from '../../utils/UHttp';

const API_URL = process.env.REACT_APP_URL_SERVER;

/**
 * Statut du compte Stripe Connect
 */
export interface StripeConnectStatus {
  isConnected: boolean;
  accountId: string | null;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  currentlyDue: string[];
  eventuallyDue: string[];
  pastDue: string[];
}

/**
 * Badge de statut pour l'affichage UI
 */
export type StripeStatusBadge = 'not_configured' | 'pending' | 'verified' | 'restricted';

/**
 * Lien d'onboarding Stripe
 */
export interface StripeOnboardingLink {
  url: string;
  expiresAt: number;
}

/**
 * Lien vers le dashboard Stripe Express
 */
export interface StripeDashboardLink {
  url: string;
}

/**
 * Statistiques de revenus
 */
export interface RevenueStats {
  totalRevenue: number;
  totalTransactions: number;
  averageTransaction: number;
  totalStripeFees?: number; // Optionnel pour compatibilité
  totalFees?: number; // Le backend renvoie totalFees au lieu de totalStripeFees
  period?: {
    startDate: string;
    endDate: string;
  };
}

/**
 * Détails d'un paiement
 */
export interface PaymentDetails {
  id: string;
  bookingId: string;
  clientId: string;
  clientName: string;
  clientEmail: string;
  stripePaymentIntentId: string;
  stripeChargeId: string | null;
  stripeRefundId: string | null;
  amount: number;
  amountPaid: number;
  depositPercentage: number | null;
  discountAmount: number;
  finalAmount: number;
  stripeFee: number;
  totalChargedToClient: number;
  promoCodeUsed: string | null;
  status: 'pending' | 'succeeded' | 'failed' | 'refunded' | 'partially_refunded';
  paymentType: 'deposit' | 'full';
  currency: string;
  refundReason: string | null;
  createdAt: string;
  updatedAt: string;
  metadata?: Record<string, any>;
}

/**
 * Liste paginée de paiements
 */
export interface PaymentList {
  payments: PaymentDetails[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Requête pour créer une session de paiement
 */
export interface CreateCheckoutSessionRequest {
  bookingId: string;
  successUrl: string;
  cancelUrl: string;
}

/**
 * Réponse de création de session de paiement
 */
export interface CreateCheckoutSessionResponse {
  sessionId: string;
  url: string;
  paymentId: string;
}

/**
 * Statut de la session de paiement
 */
export interface CheckoutSessionStatus {
  status: 'open' | 'complete' | 'expired';
  paymentStatus: 'paid' | 'unpaid' | 'no_payment_required';
  paymentId?: string;
}

/**
 * Requête de remboursement
 */
export interface RefundRequest {
  reason?: string;
  amount?: number; // Si non spécifié, remboursement total
}

/**
 * Réponse de remboursement
 */
export interface RefundResponse {
  success: boolean;
  refundId: string;
  amount: number;
  status: string;
}

/**
 * Réponses API génériques
 */
export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
}

/**
 * Classe modèle pour les appels API Stripe Connect
 */
export class StripeConnectModel {
  private static instance: StripeConnectModel;

  private constructor() {}

  public static getInstance(): StripeConnectModel {
    if (!StripeConnectModel.instance) {
      StripeConnectModel.instance = new StripeConnectModel();
    }
    return StripeConnectModel.instance;
  }

  /**
   * Récupère le statut du compte Stripe Connect
   */
  async getStatus(): Promise<ApiResponse<StripeConnectStatus>> {
    try {
      const response = await UHttp.get(
        `${API_URL}/api/stripe-connect/status`,
        UHttp.authHeaders()
      );

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          message: errorData.message || 'Erreur lors de la récupération du statut'
        };
      }

      const data = await response.json();

      const backendData = data.data;

      const statusData: StripeConnectStatus = {
        isConnected: backendData.hasAccount || false,
        accountId: null,
        chargesEnabled: backendData.chargesEnabled || false,
        payoutsEnabled: backendData.payoutsEnabled || false,
        detailsSubmitted: backendData.isVerified || false,
        currentlyDue: backendData.requirements || [],
        eventuallyDue: [],
        pastDue: []
      };

      return {
        success: true,
        message: 'Statut récupéré avec succès',
        data: statusData
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Erreur réseau'
      };
    }
  }

  /**
   * Crée un lien d'onboarding Stripe
   */
  async createOnboardingLink(refreshUrl: string, returnUrl: string): Promise<ApiResponse<{ url: string }>> {
    try {
      const response = await UHttp.post(
        `${API_URL}/api/stripe-connect/onboarding`,
        { refreshUrl, returnUrl },
        UHttp.authHeaders()
      );

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          message: errorData.message || 'Erreur lors de la création du lien d\'onboarding'
        };
      }

      const data = await response.json();

      // Le backend peut renvoyer soit { data: { url } } soit directement { url }
      const url = data.data?.url || data.url;

      if (!url) {
        console.error('Réponse backend onboarding:', data);
        return {
          success: false,
          message: 'URL manquante dans la réponse'
        };
      }

      return {
        success: true,
        message: 'Lien créé avec succès',
        data: { url }
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Erreur réseau'
      };
    }
  }

  /**
   * Crée un lien vers le dashboard Stripe Express
   */
  async createDashboardLink(): Promise<ApiResponse<{ url: string }>> {
    try {
      const response = await UHttp.post(
        `${API_URL}/api/stripe-connect/dashboard`,
        {},
        UHttp.authHeaders()
      );

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          message: errorData.message || 'Erreur lors de la création du lien dashboard'
        };
      }

      const data = await response.json();

      const url = data.data?.url || data.url;

      if (!url) {
        console.error('Réponse backend dashboard:', data);
        return {
          success: false,
          message: 'URL manquante dans la réponse'
        };
      }

      return {
        success: true,
        message: 'Lien créé avec succès',
        data: { url }
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Erreur réseau'
      };
    }
  }

  /**
   * Récupère les statistiques de revenus
   */
  async getRevenueStats(period?: 'day' | 'week' | 'month' | 'all'): Promise<ApiResponse<RevenueStats>> {
    try {
      const params = new URLSearchParams();
      if (period && period !== 'all') params.append('period', period);

      const url = `${API_URL}/api/stripe-connect/payments/stats${params.toString() ? '?' + params.toString() : ''}`;

      const response = await UHttp.get(url, UHttp.authHeaders());

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          message: errorData.message || 'Erreur lors de la récupération des statistiques'
        };
      }

      const data = await response.json();
      return {
        success: true,
        message: 'Statistiques récupérées avec succès',
        data: data.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Erreur réseau'
      };
    }
  }

  /**
   * Récupère la liste des paiements
   */
  async getPayments(
    page: number = 1,
    limit: number = 20,
    status?: string,
    startDate?: string,
    endDate?: string
  ): Promise<ApiResponse<PaymentList>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });

      if (status) params.append('status', status);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const url = `${API_URL}/api/stripe-connect/payments?${params.toString()}`;

      const response = await UHttp.get(url, UHttp.authHeaders());

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          message: errorData.message || 'Erreur lors de la récupération des paiements'
        };
      }

      const data = await response.json();
      return {
        success: true,
        message: 'Paiements récupérés avec succès',
        data: data.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Erreur réseau'
      };
    }
  }

  /**
   * Récupère les détails d'un paiement
   */
  async getPaymentDetails(paymentId: string): Promise<ApiResponse<PaymentDetails>> {
    try {
      const response = await UHttp.get(
        `${API_URL}/api/stripe-connect/payments/${paymentId}`,
        UHttp.authHeaders()
      );

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          message: errorData.message || 'Erreur lors de la récupération du paiement'
        };
      }

      const data = await response.json();
      return {
        success: true,
        message: 'Paiement récupéré avec succès',
        data: data.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Erreur réseau'
      };
    }
  }

  /**
   * Crée un remboursement
   */
  async createRefund(paymentId: string, refundData: RefundRequest): Promise<ApiResponse<RefundResponse>> {
    try {
      const response = await UHttp.post(
        `${API_URL}/api/stripe-connect/payments/${paymentId}/refund`,
        refundData,
        UHttp.authHeaders()
      );

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          message: errorData.message || 'Erreur lors du remboursement'
        };
      }

      const data = await response.json();
      return {
        success: true,
        message: 'Remboursement effectué avec succès',
        data: data.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Erreur réseau'
      };
    }
  }
}
